import { useEffect, useMemo, useState } from "react";

/**
 * Client-side paging for a list the page already holds in full.
 *
 * These endpoints return the whole collection (only the credits ledger takes
 * limit/offset), so paging here is about what the table renders, not what is
 * fetched — a hundred rows at a time instead of every row the account has.
 *
 * `resetKey` is whatever narrows the list — a search term, a filter — so typing
 * a new search puts you back on page one instead of on an empty page four. The
 * current page is also clamped to the last page, so deleting the only row on the
 * final page leaves you on a page that still has rows.
 */
export function usePagedList(items, pageSize = 20, resetKey) {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(page, totalPages);

  const visible = useMemo(
    () => items.slice((current - 1) * pageSize, current * pageSize),
    [items, current, pageSize]
  );

  return { page: current, setPage, totalPages, visible, total: items.length, pageSize };
}

export default usePagedList;
